import AsyncDataTable from '@/components/core/async-data-table/async-data-table';
import {
  DatableColumnType,
  FilterType,
  propertyType,
} from '@/components/core/async-data-table/type';
import { endpoints } from '@/api/endpoints/endpoints';
import FilterHeader from '@/components/core/async-data-table/filter-header';
import ActionPopover from '@/components/action/action-popover';
import { Plus } from 'lucide-react';
import { useLocation } from 'wouter';
import { useDeleteUser } from '@/services/user-service';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { routes } from '@/routes/route-constants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const UserAsyncTable = () => {
  const [, setLocation] = useLocation();
  const deleteUser = useDeleteUser();
  const [refreshPage, setRefreshPage] = useState(false);
  const { toast } = useToast();
  const handleDelete = async (userId: string) => {
    try {
      await deleteUser.executeAsync(userId);
      toast({
        title: 'Success',
        description: 'User deleted successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete user',
        variant: 'destructive',
      });
    }
    setRefreshPage(!refreshPage);
  };

  const cols: DatableColumnType[] = [
    {
      name: 'username',
      id: 'username',
      label: 'Username',
      searchable: true,
      orderable: true,
      datatype: 'string',
    },
    {
      name: 'firstName',
      id: 'firstName',
      label: 'First name',
      searchable: true,
      orderable: true,
      datatype: 'string',
    },
    {
      name: 'lastName',
      id: 'lastName',
      label: 'Last name',
      searchable: true,
      orderable: true,
      datatype: 'string',
    },

    {
      name: 'email',
      id: 'email',
      label: 'Email',
      searchable: false,
      orderable: false,
      datatype: 'string',
    },
    {
      name: 'roles',
      id: 'roles',
      label: 'Role',
      searchable: true,
      orderable: true,
      datatype: 'string',
    },
    {
      name: 'enabled',
      id: 'enabled',
      label: 'Enable',
      searchable: false,
      orderable: true,
      datatype: 'custom',
      Render: ({ row }: { row: any }) => {
        return <span className="text-sm text-gray-700">{row.enabled ? 'Active' : 'Inactive'}</span>;
      },
    },
    {
      name: 'createdAt',
      id: 'createdAt',
      label: 'Created',
      searchable: false,
      orderable: true,
      datatype: 'custom',
      Render: ({ row }: { row: any }) => {
        if (!row.createdAt) return <span className="text-sm text-gray-400">-</span>;
        const date = new Date(row.createdAt);
        return (
          <span className="text-sm text-gray-700">
            {date.toLocaleDateString()} {date.toLocaleTimeString()}
          </span>
        );
      },
    },
    {
      name: 'action',
      id: 'action',
      label: 'Action',
      Render: ({ row }: { row: any }) => (
        <ActionPopover
          onEdit={() => {
            setLocation(routes.user.edit(row.id));
          }}
          onDelete={() => {
            handleDelete(row.id);
          }}
        />
      ),
    },
  ];
  const handleClickRow = (row: any) => {
    setLocation(routes.user.view(row.id));
  };

  return (
    <AsyncDataTable
      dataUrl={endpoints.user.list}
      columns={cols}
      autoIndex
      FilterComponent={FilterHeader}
      refreshPage={refreshPage}
      showPageHeader={false}
      pageTitle="Users"
      pageSubtitle="Manage users"
      orderByDef="updated_at"
      headerAction={() => {
        setLocation('/users/new');
      }}
      onClickRow={handleClickRow}
      headerActionText="Create User"
      headerActionIcon={<Plus size={18} />}
    />
  );
};

export default UserAsyncTable;
